import React, { useState, useCallback } from 'react';
import { LogoService, UploadedLogo } from '../services/logoService';
import { useAuth } from '../contexts/AuthContext';

interface LogoUploadProps {
  onLogoUploaded: (logo: UploadedLogo) => void;
  onError?: (error: string) => void;
  className?: string;
}

const LogoUpload: React.FC<LogoUploadProps> = ({ 
  onLogoUploaded, 
  onError,
  className = '' 
}) => {
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileUpload = useCallback(async (file: File) => {
    if (!currentUser) {
      onError?.('You must be logged in to upload logos');
      return;
    }

    try {
      setUploading(true);
      
      const uploadedLogo = await LogoService.uploadLogo(file);
      onLogoUploaded(uploadedLogo);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      console.error('❌ Logo upload error:', errorMessage);
      onError?.(errorMessage);
    } finally {
      setUploading(false);
    }
  }, [currentUser, onLogoUploaded, onError]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset input value to allow uploading the same file again
    event.target.value = '';
  }, [handleFileUpload]);

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    
    const files = Array.from(event.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleFileUpload(imageFile);
    } else {
      onError?.('Please drop an image file');
    }
  }, [handleFileUpload, onError]);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  if (!currentUser) {
    return (
      <div className={`text-center p-4 border-2 border-gray-300 border-dashed rounded-lg ${className}`}>
        <p className="text-sm text-gray-500">
          Please log in to upload logos
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver
            ? 'border-blue-500 bg-blue-50'
            : uploading
            ? 'border-gray-400 bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        {uploading ? (
          <div className="space-y-3">
            <div className="w-8 h-8 mx-auto border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-gray-600">
              Uploading logo to Firebase...
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="mx-auto w-12 h-12 text-gray-400">
              <svg 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                />
              </svg>
            </div>
            
            <div>
              <label htmlFor="logo-upload" className="cursor-pointer">
                <span className="text-sm font-medium text-blue-600 hover:text-blue-500">
                  Click to upload a logo
                </span>
                <span className="text-sm text-gray-500"> or drag and drop</span>
              </label>
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={uploading}
              />
            </div>
            
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF, WebP or SVG up to 5MB
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogoUpload;
